import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notasFiscaisApi, contasApi, categoriasApi, cartoesApi } from '../../api/endpoints'
import { Button, IconButton } from '../../components/ui/Button'
import { Input, Select, Toggle } from '../../components/ui/Input'
import { Card, CardHeader } from '../../components/ui/Card'
import { LoadingState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import { OrigemNota, ModalidadeCartao } from '../../lib/enums'
import type { ItemCompradoGrupo } from '../../types'
import { formatMoney, formatDate } from '../../lib/utils'

export const AJUDA_RECORRENCIA =
  'Mensal: comprado em todos os meses da janela menos um (padrão: 2 dos últimos 3 meses fechados). Anual: comprado cerca de uma vez por ano. Avulso: todo o resto. Tudo nasce Avulso, e você pode marcar à mão ao abrir um item.'

const num = (t: string) => Number(t.replace(',', '.')) || 0

interface Linha { descricao: string; quantidade: string; unidade: string; valor: string }
const linhaVazia = (): Linha => ({ descricao: '', quantidade: '1', unidade: 'UN', valor: '' })

/** Compra sem nota fiscal (açougue, feira): entra no histórico de preço, no resumo e nas sugestões como qualquer nota. */
export function CompraSemNotaModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [loja, setLoja] = useState('')
  const [data, setData] = useState(new Date().toISOString().slice(0, 10))
  const [linhas, setLinhas] = useState<Linha[]>([linhaVazia()])
  const [lancar, setLancar] = useState(false)
  const [contaId, setContaId] = useState('')
  const [categoriaId, setCategoriaId] = useState('')
  const [pagoCom, setPagoCom] = useState<'conta' | 'cartao'>('conta')
  const [cartaoId, setCartaoId] = useState('')
  const [parcelas, setParcelas] = useState('1')
  const [debito, setDebito] = useState(false)

  const { data: sugestoes } = useQuery({ queryKey: ['itens-sugestoes'], queryFn: () => notasFiscaisApi.sugestoesCompra(), enabled: open, staleTime: 60_000 })
  const { data: contas = [] } = useQuery({ queryKey: ['contas'], queryFn: () => contasApi.list(), enabled: open && lancar })
  const { data: categorias = [] } = useQuery({ queryKey: ['categorias'], queryFn: () => categoriasApi.list(), enabled: open && lancar })

  const { data: cartoes = [] } = useQuery({ queryKey: ['cartoes'], queryFn: cartoesApi.list, enabled: open && lancar })
  const cartaoEscolhido = cartoes.find((c) => c.id === cartaoId)
  const cartaoComDebito = !!cartaoEscolhido && cartaoEscolhido.modalidade === ModalidadeCartao.Credito && cartaoEscolhido.temDebito
  const creditoParcelavel = pagoCom === 'cartao' && !!cartaoEscolhido && cartaoEscolhido.modalidade === ModalidadeCartao.Credito && !(cartaoComDebito && debito)
  const parcelado = creditoParcelavel && (Math.floor(Number(parcelas)) || 1) > 1

  const atualizar = (i: number, p: Partial<Linha>) => setLinhas((ls) => ls.map((l, k) => (k === i ? { ...l, ...p } : l)))
  const validas = linhas.filter((l) => l.descricao.trim() && num(l.valor) > 0 && num(l.quantidade) > 0)
  const total = validas.reduce((s, l) => s + num(l.valor), 0)
  const pode = loja.trim().length > 0 && !!data && validas.length > 0 && (!lancar || (pagoCom === 'conta' ? !!contaId : !!cartaoId && !parcelado))

  const salvar = useMutation({
    mutationFn: () => notasFiscaisApi.criar({
      nomeEmitente: loja.trim(), dataEmissao: data, valorTotal: Math.round(total * 100) / 100, origem: OrigemNota.Manual,
      itens: validas.map((l) => {
        const qtd = num(l.quantidade)
        const valor = num(l.valor)
        return { descricao: l.descricao.trim(), quantidade: qtd, unidade: l.unidade, valorTotal: valor, valorUnitario: Math.round((valor / qtd) * 10000) / 10000 }
      }),
      ...(lancar ? (pagoCom === 'cartao'
        ? { cartaoId, debito: cartaoComDebito && debito, categoriaId: categoriaId || undefined, descricao: loja.trim() }
        : { accountId: contaId, categoriaId: categoriaId || undefined, descricao: loja.trim() }) : {}),
    }),
    onSuccess: () => {
      qc.invalidateQueries()
      toast.success('Compra salva.')
      setLoja(''); setLinhas([linhaVazia()]); setLancar(false)
      onClose()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Modal
      open={open} onClose={onClose} title="Compra sem nota" size="lg"
      footer={<><Button variant="light" onClick={onClose}>Cancelar</Button><Button icon="ri-save-line" disabled={!pode} loading={salvar.isPending} onClick={() => salvar.mutate()}>Salvar · {formatMoney(total)}</Button></>}
    >
      <div className="space-y-4">
        <p className="text-13 text-muted">Para compras sem nota fiscal (açougue, feira). Informe o valor pago de cada item e a quantidade; o preço por kg, litro ou unidade é calculado sozinho.</p>
        <datalist id="sugestoes-lojas">{(sugestoes?.lojas ?? []).map((l) => <option key={l} value={l} />)}</datalist>
        <datalist id="sugestoes-itens">{(sugestoes?.itens ?? []).map((l) => <option key={l} value={l} />)}</datalist>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Loja" list="sugestoes-lojas" value={loja} onChange={(e) => setLoja(e.target.value)} placeholder="Ex.: Açougue do Zé" />
          <Input label="Data da compra" type="date" value={data} onChange={(e) => setData(e.target.value)} />
        </div>

        {linhas.map((l, i) => (
          <div key={i} className="grid items-end gap-3 rounded border border-border/70 p-3 sm:grid-cols-[2fr_1fr_1fr_1fr_auto]">
            <Input label="Descrição" list="sugestoes-itens" value={l.descricao} onChange={(e) => atualizar(i, { descricao: e.target.value })} placeholder="Ex.: Carne moída" />
            <Select label="Unidade" value={l.unidade} onChange={(e) => atualizar(i, { unidade: e.target.value })}>
              <option value="UN">Unidade</option><option value="KG">Quilo (kg)</option><option value="L">Litro (L)</option>
            </Select>
            <Input label="Quantidade" type="number" min={0} step="0.001" value={l.quantidade} onChange={(e) => atualizar(i, { quantidade: e.target.value })} />
            <Input label="Valor pago (R$)" type="number" min={0} step="0.01" value={l.valor} onChange={(e) => atualizar(i, { valor: e.target.value })} />
            {linhas.length > 1 ? <IconButton icon="ri-delete-bin-line" label="Remover item" onClick={() => setLinhas((ls) => ls.filter((_, k) => k !== i))} /> : <span />}
          </div>
        ))}
        <Button size="sm" variant="light" icon="ri-add-line" onClick={() => setLinhas((ls) => [...ls, linhaVazia()])}>Adicionar item</Button>

        <div className="space-y-3 rounded bg-surface2 p-3">
          <Toggle label={`Lançar como despesa de ${formatMoney(total)}`} checked={lancar} onChange={setLancar} />
          <p className="text-2xs text-muted">Cria o lançamento na conta escolhida. Sem isso, só os itens são guardados (útil se você já lançou esse gasto).</p>
          {lancar && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Select label="Pago com" value={pagoCom} onChange={(e) => setPagoCom(e.target.value as 'conta' | 'cartao')}>
                <option value="conta">Conta</option><option value="cartao">Cartão</option>
              </Select>
              {pagoCom === 'conta' ? (
                <Select label="Conta" value={contaId} onChange={(e) => setContaId(e.target.value)}>
                  <option value="">Selecione...</option>
                  {contas.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              ) : (
                <Select label="Cartão" value={cartaoId} onChange={(e) => { setCartaoId(e.target.value); setDebito(false) }}>
                  <option value="">Selecione...</option>
                  {cartoes.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </Select>
              )}
              {cartaoComDebito && (
                <Select label="Como pagou" value={debito ? 'debito' : 'credito'} onChange={(e) => setDebito(e.target.value === 'debito')}>
                  <option value="credito">Crédito (entra na fatura)</option><option value="debito">Débito (sai da conta na hora)</option>
                </Select>
              )}
              {creditoParcelavel && <Input label="Parcelas" type="number" min={1} max={24} value={parcelas} onChange={(e) => setParcelas(e.target.value)} />}
              {parcelado && <p className="sm:col-span-2 text-13 font-medium text-expense">Compra parcelada não se lança por aqui. Lance a compra no cartão em Novo lançamento (com as parcelas) e depois vincule a nota por lá.</p>}
              <Select label="Categoria (opcional)" value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)}>
                <option value="">Sem categoria</option>
                {categorias.map((c) => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
              </Select>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

/** Ligação grupo → categoria do Orçamento, minhas correções e configuração do cálculo Mensal/Anual/Avulso. */
export function CategoriasItens() {
  const qc = useQueryClient()
  const toast = useToast()
  const { data: g, isLoading } = useQuery({ queryKey: ['itens-gestao'], queryFn: () => notasFiscaisApi.gestaoItens() })
  const { data: catalogo } = useQuery({ queryKey: ['itens-catalogo'], queryFn: () => notasFiscaisApi.catalogoItens(), staleTime: Infinity })
  const { data: categoriasOrc = [] } = useQuery({ queryKey: ['categorias'], queryFn: () => categoriasApi.list() })

  const refaz = () => { qc.invalidateQueries({ queryKey: ['itens-gestao'] }); qc.invalidateQueries({ queryKey: ['itens-comprados'] }); qc.invalidateQueries({ queryKey: ['itens-resumo'] }) }
  const onError = (e: Error) => toast.error(e.message)
  const ligar = useMutation({ mutationFn: (v: { grupo: string; categoriaId: string }) => notasFiscaisApi.ligarGrupoOrcamento(v.grupo, v.categoriaId || null), onSuccess: refaz, onError })
  const apagar = useMutation({ mutationFn: (id: string) => notasFiscaisApi.apagarRegraCategoria(id), onSuccess: refaz, onError })
  const config = useMutation({ mutationFn: notasFiscaisApi.salvarConfiguracaoItens, onSuccess: refaz, onError })

  if (isLoading || !g) return <LoadingState />

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader title="Ligar ao Orçamento" icon="ri-links-line" />
        <div className="space-y-3 p-5">
          <p className="text-13 text-muted">Ligue um grande grupo a uma categoria do Orçamento (ex.: Alimentação → Mercado). Com a ligação, o botão "Sugerir limite" no Orçamento mostra quanto de cada grupo você costuma gastar.</p>
          {g.grupos.map((gr) => (
            <div key={gr.codigo} className="grid items-center gap-3 sm:grid-cols-[1fr_16rem]">
              <div>
                <p className="font-medium text-white">{gr.nome}</p>
                <p className="text-2xs text-muted">{(catalogo?.categorias ?? []).filter((c) => c.grupo === gr.codigo).map((c) => c.nome).join(', ')}</p>
              </div>
              <Select label="" value={gr.categoriaOrcamentoId ?? ''} onChange={(e) => ligar.mutate({ grupo: gr.codigo, categoriaId: e.target.value })}>
                <option value="">Sem ligação</option>
                {categoriasOrc.map((c) => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
              </Select>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <CardHeader title="Minhas correções" icon="ri-price-tag-3-line" />
        <div className="space-y-2 p-5">
          {g.regras.length === 0 ? (
            <p className="text-13 text-muted">Você ainda não corrigiu nenhuma categoria. Em Itens comprados, abra um item e use "Ajustar categoria e cobertura".</p>
          ) : g.regras.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-white">{r.descricao}</p>
                <p className="text-2xs text-muted">{r.categoriaNome} · {r.vigenteDesde ? `daqui pra frente (${formatDate(r.vigenteDesde)})` : 'todas as notas'}</p>
              </div>
              <IconButton icon="ri-delete-bin-line" label="Apagar correção" onClick={() => apagar.mutate(r.id)} />
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <CardHeader title="Como calculo Mensal, Anual e Avulso" icon="ri-calendar-check-line" />
        <div className="space-y-3 p-5">
          <p className="text-13 text-muted">{AJUDA_RECORRENCIA}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Select label="Janela (meses fechados)" value={String(g.configuracao.janelaMeses)} onChange={(e) => config.mutate({ ...g.configuracao, janelaMeses: Number(e.target.value) })}>
              {[2, 3, 4, 6, 12].map((n) => <option key={n} value={n}>{n} meses</option>)}
            </Select>
            <Select label="Valor esperado por mês" value={g.configuracao.metodo} onChange={(e) => config.mutate({ ...g.configuracao, metodo: e.target.value as 'mediana' | 'media' })}>
              <option value="mediana">Mediana</option><option value="media">Média</option>
            </Select>
          </div>
          <p className="text-2xs text-muted">A mediana ignora um mês atípico (uma compra grande); a média considera todos.</p>
        </div>
      </Card>
    </div>
  )
}

/** Conta quanto você tem em casa agora; a estimativa parte daí e desconta o consumo esperado por mês. */
export function EstoqueModal({ g, itemId, onClose }: { g: ItemCompradoGrupo; itemId: string; onClose: () => void }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [qtd, setQtd] = useState(g.estoqueContado && g.estoqueAtual != null ? String(g.estoqueAtual) : '')
  const unidade = g.unidadePadrao.toLowerCase()
  const salvar = useMutation({
    mutationFn: (q: number | null) => notasFiscaisApi.definirEstoqueItem(itemId, q),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['itens-comprados'] }); toast.success('Estoque atualizado.'); onClose() },
    onError: (e: Error) => toast.error(e.message),
  })
  const valido = qtd.trim() !== '' && num(qtd) >= 0

  return (
    <Modal
      open onClose={onClose} title="Quanto você tem em casa?" size="sm"
      footer={<>
        {g.estoqueContado && <Button variant="light" onClick={() => salvar.mutate(null)}>Apagar contagem</Button>}
        <Button icon="ri-save-line" disabled={!valido} loading={salvar.isPending} onClick={() => salvar.mutate(num(qtd))}>Salvar</Button>
      </>}
    >
      <div className="space-y-3">
        <p className="text-13 text-white">{g.descricao}</p>
        <p className="text-2xs text-muted">Informe o que sobrou agora ({unidade}). O Walleon parte dessa contagem, soma as próximas compras e desconta o consumo esperado por mês para estimar quando acaba.</p>
        <Input label={`Quantidade (${unidade})`} type="number" min={0} step="0.001" value={qtd} onChange={(e) => setQtd(e.target.value)} />
      </div>
    </Modal>
  )
}

/** Compra grande (promoção, atacado): quanto comprar para cobrir N meses, quanto custa e quanto separar por mês até lá. */
export function PlanoCompraModal({ g, onClose }: { g: ItemCompradoGrupo; onClose: () => void }) {
  const [meses, setMeses] = useState('6')
  const [antecedencia, setAntecedencia] = useState('3')
  const m = Math.max(1, Math.floor(num(meses)) || 1)
  const ant = Math.max(1, Math.floor(num(antecedencia)) || 1)
  const unidade = g.unidadePadrao.toLowerCase()
  const necessaria = g.valorEsperadoMensal * m
  const aComprar = Math.max(0, necessaria - (g.estoqueAtual ?? 0))
  const custo = aComprar * g.precoPadrao

  return (
    <Modal open onClose={onClose} title="Planejar compra grande" size="sm">
      <div className="space-y-3">
        <p className="text-13 text-white">{g.descricao}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Cobrir quantos meses?" type="number" min={1} max={24} value={meses} onChange={(e) => setMeses(e.target.value)} />
          <Input label="Vai comprar daqui a quantos meses?" type="number" min={1} max={24} value={antecedencia} onChange={(e) => setAntecedencia(e.target.value)} />
        </div>
        <p className="text-13 text-white">Consumo esperado: {g.valorEsperadoMensal} {unidade} por mês. Para {m} meses: {necessaria.toFixed(2)} {unidade}.</p>
        <p className="text-13 text-white">
          Você tem {g.estoqueAtual != null ? `${g.estoqueAtual} ${unidade}` : 'estoque desconhecido (use "Contar estoque")'}
          {g.estoqueAcabaEm ? `, acabando por volta de ${formatDate(g.estoqueAcabaEm)}` : ''}.
        </p>
        <p className="text-base font-semibold text-primary">Comprar {aComprar.toFixed(2)} {unidade} ≈ {formatMoney(custo)}</p>
        <p className="text-2xs text-muted">No preço da última compra ({formatMoney(g.precoPadrao)}/{unidade}). Para ter esse valor na hora, separe cerca de {formatMoney(custo / ant)} por mês: crie uma reserva no Orçamento (Acumular saldo) com esse limite.</p>
      </div>
    </Modal>
  )
}
